// Die verbundenen Kontakte einer Zeile der Kontaktliste (Slice 6c, ST-SEA-001,
// CM-REL-002, CM-PTY-011): an einer Organisation ihre Ansprechpartner:innen, an
// einer Person mit "und weitere" ihre Organisationen (Restliste C5) -- dieselben
// Kantentypen, von der anderen Seite gelesen. Eingerueckt unter der Zeile,
// geladen erst beim Aufklappen. Die Namen hier sagen deshalb Gegenseite, nicht
// Person: an einer Person stehen Organisationen.
//
// KEINE NEUE SCHNITTSTELLE: dieselbe Leseroute wie der Abschnitt
// "Beziehungen" am Kontakt (GET /api/parties/{id}/relationships) mit
// derselben Sichtbarkeit (TR-001) und derselben Teilung in Ansprechpartner:innen
// (works_for, contributes_to, leads). Die Route liefert 50 Kanten je Seite;
// "Weitere laden" blaettert ueber ihren Cursor, bis jede
// sichtbare erreicht ist -- die Seitengroesse ist eine technische und keine
// fachliche Grenze.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Caption1, Link, makeStyles, Spinner, tokens } from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import { partyHref } from '../parties.ts';
import { CONTACT_PERSON_TYPES, fetchRelationshipPage, roleLabel, type RelationshipView } from '../relationships.ts';
import { AppLink } from '../shell/router.tsx';
import { Vorgang } from '../shell/zustand.tsx';

const useStyles = makeStyles({
  liste: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.375rem' },
  eintrag: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: '0.5rem' },
  name: { color: tokens.colorNeutralForeground1, fontWeight: tokens.fontWeightSemibold },
  hinweis: { color: tokens.colorNeutralForeground3 },
  zeile: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' },
});

/**
 * Neben dem Namen der Person: die Funktion, sonst die Art der Beziehung aus
 * ihrer Sicht. Die Lesart der Route steht aus Sicht der Organisation ("wird
 * geleitet von") und laese sich neben der Person verkehrt herum; works_for
 * ist der Normalfall und braucht kein Wort.
 */
const ART_NEBEN_DER_PERSON: Readonly<Record<string, string>> = { leads: 'Leitung', contributes_to: 'freie Mitarbeit' };

interface Gegenseite {
  readonly partyId: string;
  readonly displayName: string;
  /** Funktion je Kante, sonst die Art (ohne works_for); jede einmal. */
  readonly funktionen: readonly string[];
}

/**
 * EINE ZEILE JE PERSON, nicht je Kante: wer fuer eine Organisation arbeitet und
 * sie leitet, traegt zwei Kanten (die Eindeutigkeit schliesst den Typ ein), ist
 * aber eine Ansprechperson -- so zaehlt auch die Liste der Suche
 * (`contactPersonOfPartyId`), und "Alle N" muss dieselbe Zahl nennen.
 */
function nachGegenseite(kanten: readonly RelationshipView[]): readonly Gegenseite[] {
  const gegenseiten = new Map<string, { partyId: string; displayName: string; funktionen: string[] }>();
  for (const kante of kanten) {
    const gegenseite = gegenseiten.get(kante.counterpartPartyId) ?? { partyId: kante.counterpartPartyId, displayName: kante.counterpartDisplayName, funktionen: [] };
    gegenseiten.set(kante.counterpartPartyId, gegenseite);
    // Die Lesart steht aus Sicht der Organisation ("beschäftigt"); neben dem
    // Namen der Person sagt die Funktion mehr. Ohne Funktion nennt nur eine
    // andere Art als works_for ihre Lesart.
    const funktion = roleLabel(kante.roleCode) ?? ART_NEBEN_DER_PERSON[kante.relationshipTypeCode];
    if (funktion !== undefined && !gegenseite.funktionen.includes(funktion)) gegenseite.funktionen.push(funktion);
  }
  return [...gegenseiten.values()];
}

type Stand =
  | { readonly art: 'laedt' }
  | {
      readonly art: 'geladen';
      readonly kanten: readonly RelationshipView[];
      /** Wo die naechste Seite ansetzt; undefined am Ende. */
      readonly cursor: string | undefined;
      /** Weitere Kanten ohne Cursor: eine API vor dem Blaettern. */
      readonly weitereOhneCursor: boolean;
    }
  | { readonly art: 'fehler'; readonly meldung: PlainMessage }
  | { readonly art: 'unbekannt' };

type Nachladen =
  | { readonly art: 'ruht' }
  | { readonly art: 'laedt' }
  | { readonly art: 'fertig'; readonly neu: number }
  | { readonly art: 'fehler'; readonly meldung: PlainMessage };

/** Was aufklappt: die Ansprechpartner:innen einer Organisation oder die Organisationen einer Person. */
export type AufklappSeite = 'organisation' | 'person';

const WORTE: Readonly<Record<AufklappSeite, { readonly mehrzahl: string; readonly weg: string }>> = {
  organisation: { mehrzahl: 'Ansprechpartner:innen', weg: 'Diese Organisation ist nicht mehr da.' },
  person: { mehrzahl: 'Organisationen', weg: 'Dieser Kontakt ist nicht mehr da.' },
};

function nichtErreichbar(mehrzahl: string): PlainMessage {
  return {
    headline: 'LIAISON ist gerade nicht erreichbar.',
    explanation: `Die ${mehrzahl} ließen sich nicht laden.`,
    nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
    retryable: true,
  };
}

/** Kein Token: die Neuanmeldung laeuft, eine Fehlermeldung waere falsch. */
class AbgebrochenFehler extends Error {}

/** Eine Zeile aus Ueberschrift, Erklaerung und naechstem Schritt; manche Ueberschrift endet ohne Punkt. */
function meldungsText(meldung: PlainMessage): string {
  return [meldung.headline, meldung.explanation, meldung.nextStep]
    .filter((teil) => teil !== '')
    .map((teil) => (/[.!?]$/.test(teil) ? teil : `${teil}.`))
    .join(' ');
}

/**
 * Kanten ab `cursor`, bis mindestens eine neue Gegenseite gefunden oder das
 * Ende erreicht ist: eine Seite kann nur andere Kanten tragen, und ein Klick ohne neuen
 * Eintrag hiesse "da ist nichts" -- falsch, solange es weitergeht.
 */
async function kantenAb(
  laden: (cursor: string | undefined) => ReturnType<typeof fetchRelationshipPage>,
  cursor: string | undefined,
  // Schon gezeigte Gegenseiten: eine Seite mit nur weiteren Kanten von ihnen ist
  // fuer den Klick "nichts Neues" und blaettert weiter.
  bekannt: ReadonlySet<string> = new Set(),
): Promise<Exclude<Stand, { art: 'laedt' }>> {
  const kanten: RelationshipView[] = [];
  let weiter = cursor;
  for (;;) {
    const seite = await laden(weiter);
    if (seite.outcome === 'not-found') return { art: 'unbekannt' };
    if (seite.outcome === 'rejected') return { art: 'fehler', meldung: seite.message };
    // An einer Person zeigen dieselben Typen auf ihre Organisationen.
    kanten.push(...seite.relationships.filter((kante) => CONTACT_PERSON_TYPES.includes(kante.relationshipTypeCode)));
    weiter = seite.nextCursor;
    if (kanten.some((kante) => !bekannt.has(kante.counterpartPartyId)) || weiter === undefined) {
      return { art: 'geladen', kanten, cursor: weiter, weitereOhneCursor: seite.hasMore && weiter === undefined };
    }
  }
}

export function Verbundene({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  partyId,
  name,
  onAnzahl,
  onAlsListe,
  seite = 'organisation',
}: {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly partyId: string;
  readonly name: string;
  /** Nach dem Laden die Anzahl fuer den Aufklapper, "50+" solange es weitere gibt (M-09a). */
  readonly onAnzahl?: (anzahl: string | undefined) => void;
  /**
   * „Als Liste zeigen“ (E3 S2b): die Kontaktliste sucht dann mit dem Filter
   * „Ansprechpartner:innen von <Organisation>“. Fehlt er, steht kein Knopf.
   */
  readonly onAlsListe?: () => void;
  readonly seite?: AufklappSeite;
}): ReactNode {
  const styles = useStyles();
  const { mehrzahl, weg } = WORTE[seite];
  const NICHT_ERREICHBAR = nichtErreichbar(mehrzahl);
  const [stand, setStand] = useState<Stand>({ art: 'laedt' });
  const [nachladen, setNachladen] = useState<Nachladen>({ art: 'ruht' });
  const [versuch, setVersuch] = useState(0);
  // Nach "Erneut versuchen" verschwindet der Knopf mit dem Neuladen; der Fokus
  // geht dann in die Statusregion, die das Ergebnis sagt -- nie auf BODY.
  const statusRef = useRef<HTMLDivElement>(null);
  const fokusNachLaden = useRef(false);
  // Nach "Weitere laden" steht der Fokus auf der ersten neuen Person; kam keine
  // neue und gibt es keine weitere Seite, verschwindet der Knopf -- dann die
  // Statusregion am Knopf.
  const listeRef = useRef<HTMLUListElement>(null);
  const nachladeStatusRef = useRef<HTMLDivElement>(null);
  const fokusAufNeue = useRef<number | undefined>(undefined);
  useEffect(() => {
    // Ohne geladene Liste keine Zahl am Knopf: eine alte waere eine Behauptung.
    if (stand.art === 'fehler' || stand.art === 'unbekannt') onAnzahl?.(undefined);
    if (stand.art !== 'geladen') return;
    const zahl = nachGegenseite(stand.kanten).length.toLocaleString('de-DE');
    onAnzahl?.(stand.cursor !== undefined || stand.weitereOhneCursor ? `${zahl}+` : zahl);
  }, [stand, onAnzahl]);
  useEffect(() => {
    if (fokusNachLaden.current && stand.art !== 'laedt') {
      fokusNachLaden.current = false;
      statusRef.current?.focus();
    }
  }, [stand]);
  useEffect(() => {
    const erster = fokusAufNeue.current;
    if (erster === undefined || nachladen.art === 'laedt') return;
    fokusAufNeue.current = undefined;
    // Je Eintrag genau ein Link: der n-te Link ist die n-te Person.
    if (nachladen.art === 'fertig' && nachladen.neu > 0) listeRef.current?.querySelectorAll('a').item(erster)?.focus();
  }, [nachladen]);

  const seiteLaden = useCallback(
    async (cursor: string | undefined): ReturnType<typeof fetchRelationshipPage> => {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        throw new AbgebrochenFehler();
      }
      return fetchRelationshipPage({ baseUrl: apiBaseUrl, accessToken: token, partyId, ...(cursor === undefined ? {} : { cursor }) });
    },
    [accessToken, apiBaseUrl, onReauthenticate, partyId],
  );

  // Laedt die Zeile neu, gilt ein noch laufendes Nachladen nicht mehr.
  const generation = useRef(0);
  useEffect(() => {
    let aktuell = true;
    generation.current += 1;
    setStand({ art: 'laedt' });
    setNachladen({ art: 'ruht' });
    void kantenAb(seiteLaden, undefined)
      .then((neu) => {
        if (aktuell) setStand(neu);
      })
      .catch((fehler: unknown) => {
        if (aktuell && !(fehler instanceof AbgebrochenFehler)) setStand({ art: 'fehler', meldung: NICHT_ERREICHBAR });
      });
    return () => {
      aktuell = false;
    };
  }, [seiteLaden, versuch]);

  const weitereLaden = useCallback(
    (bisher: Extract<Stand, { art: 'geladen' }>) => {
      setNachladen({ art: 'laedt' });
      const meine = generation.current;
      void kantenAb(seiteLaden, bisher.cursor, new Set(bisher.kanten.map((kante) => kante.counterpartPartyId)))
        .then((neu) => {
          if (meine !== generation.current) return;
          if (neu.art !== 'geladen') {
            setNachladen({ art: 'fehler', meldung: neu.art === 'fehler' ? neu.meldung : NICHT_ERREICHBAR });
            return;
          }
          const vorher = nachGegenseite(bisher.kanten).length;
          const alle = [...bisher.kanten, ...neu.kanten];
          const dazu = nachGegenseite(alle).length - vorher;
          fokusAufNeue.current = vorher;
          setStand({ ...neu, kanten: alle });
          setNachladen({ art: 'fertig', neu: dazu });
          // Ohne neue Person und ohne weitere Seite verschwindet der Knopf.
          if (dazu === 0) nachladeStatusRef.current?.focus();
        })
        .catch((fehler: unknown) => {
          if (meine === generation.current && !(fehler instanceof AbgebrochenFehler)) setNachladen({ art: 'fehler', meldung: NICHT_ERREICHBAR });
        });
    },
    [seiteLaden],
  );

  // Eine benannte Gruppe, damit ein Screenreader weiss, wessen Personen das sind;
  // der Fokus bleibt am Knopf, die Statusregion sagt den Stand an.
  return (
    <div role="group" aria-label={`${mehrzahl} von ${name}`}>
      <div role="status" aria-live="polite" ref={statusRef} tabIndex={-1}>
        {stand.art === 'laedt' ? <Spinner size="tiny" labelPosition="after" label={`${mehrzahl} werden geladen`} /> : undefined}
        {stand.art === 'geladen' && stand.kanten.length === 0 ? (
          <Caption1 className={styles.hinweis}>Keine {mehrzahl}.</Caption1>
        ) : undefined}
        {stand.art === 'unbekannt' ? <Caption1 className={styles.hinweis}>{weg}</Caption1> : undefined}
        {stand.art === 'fehler' ? <Caption1>{meldungsText(stand.meldung)}</Caption1> : undefined}
      </div>
      {stand.art === 'fehler' && stand.meldung.correlationId !== undefined ? (
        <Vorgang nummer={stand.meldung.correlationId} eingebettet />
      ) : undefined}
      {stand.art === 'geladen' && stand.kanten.length > 0 ? (
        <ul className={styles.liste} ref={listeRef}>
          {nachGegenseite(stand.kanten).map((gegenseite) => (
            <li key={gegenseite.partyId} className={styles.eintrag}>
              <AppLink to={partyHref(gegenseite.partyId)} className={styles.name}>
                {gegenseite.displayName}
              </AppLink>
              {gegenseite.funktionen.length === 0 ? undefined : <Caption1 className={styles.hinweis}>{gegenseite.funktionen.join(', ')}</Caption1>}
            </li>
          ))}
        </ul>
      ) : undefined}
      {/* Das Nachladen sagt sein Ergebnis AM KNOPF: bei vielen Personen laege
          die obere Region ausser Sicht, waehrend der Fokus unten steht. */}
      {stand.art === 'geladen' && (stand.cursor !== undefined || nachladen.art !== 'ruht') ? (
        <>
          <div className={styles.zeile}>
            {stand.cursor !== undefined ? (
              <Button
                appearance="secondary"
                size="small"
                aria-label={`Weitere laden: ${mehrzahl} von ${name}`}
                disabledFocusable={nachladen.art === 'laedt'}
                onClick={() => {
                  if (nachladen.art !== 'laedt') weitereLaden(stand);
                }}
              >
                Weitere laden
              </Button>
            ) : undefined}
            <div role="status" aria-live="polite" aria-label={`Weitere ${mehrzahl}`} ref={nachladeStatusRef} tabIndex={-1}>
              {nachladen.art === 'laedt' ? <Spinner size="tiny" labelPosition="after" label="Weitere werden geladen" /> : undefined}
              {nachladen.art === 'fertig' ? (
                <Caption1 className={styles.hinweis}>
                  {/* Die Gesamtzahl statt der neuen: "noch 2" laese sich wie "es kommen noch 2". */}
                  {nachladen.neu === 0
                    ? `Keine weiteren ${mehrzahl}.`
                    : stand.art === 'geladen' && stand.cursor === undefined
                      ? `Das sind alle ${nachGegenseite(stand.kanten).length} ${mehrzahl}.`
                      : `Jetzt ${stand.art === 'geladen' ? nachGegenseite(stand.kanten).length : nachladen.neu} ${mehrzahl}.`}
                </Caption1>
              ) : undefined}
              {nachladen.art === 'fehler' ? <Caption1>{meldungsText(nachladen.meldung)}</Caption1> : undefined}
            </div>
          </div>
          {nachladen.art === 'fehler' && nachladen.meldung.correlationId !== undefined ? (
            <Vorgang nummer={nachladen.meldung.correlationId} eingebettet />
          ) : undefined}
        </>
      ) : undefined}
      {/* Am Ende der Liste (DESIGN zu E3 S2b): wer die ersten sah, will alle. Der Name beginnt
          mit dem sichtbaren Text (WCAG 2.5.3) und nennt die Organisation. */}
      {onAlsListe !== undefined && stand.art === 'geladen' && stand.kanten.length > 0 ? (
        <div className={styles.zeile}>
          {(() => {
            const vollstaendig = stand.cursor === undefined && !stand.weitereOhneCursor;
            const text = vollstaendig ? `Alle ${nachGegenseite(stand.kanten).length.toLocaleString('de-DE')} als Liste zeigen` : 'Als Liste zeigen';
            return (
              // Ein Link als Knopf wie „Weitere … ansehen“ daneben: ein Muster je Zeile; subtle las
              // sich wie eine Caption (Restliste B7). Er bleibt ein Knopf, weil er sucht, nicht navigiert.
              <Link as="button" aria-label={`${text}: Ansprechpartner:innen von ${name}`} onClick={onAlsListe}>
                {text}
              </Link>
            );
          })()}
        </div>
      ) : undefined}
      {stand.art === 'geladen' && stand.weitereOhneCursor ? (
        <div className={styles.zeile}>
          <AppLink to={partyHref(partyId)}>Weitere {mehrzahl} von {name} ansehen</AppLink>
        </div>
      ) : undefined}
      {stand.art === 'fehler' && stand.meldung.retryable ? (
        <div className={styles.zeile}>
          <Button
            appearance="secondary"
            size="small"
            onClick={() => {
              // Vor dem Neuladen: der Knopf verschwindet gleich, der Fokus soll nicht mit ihm gehen.
              statusRef.current?.focus();
              fokusNachLaden.current = true;
              setVersuch((alt) => alt + 1);
            }}
          >
            Erneut versuchen
          </Button>
        </div>
      ) : undefined}
    </div>
  );
}
